/**
 * Deterministic, theme-tinted profile avatar (boring-avatars).
 *
 * Every user gets a stable generated avatar seeded by a per-user id (email),
 * so the same person always renders the same art. Colours are pulled live from
 * the platform theme tokens (`--primary` + `--chart-*`) — read in JS because
 * boring-avatars paints SVG fills that don't resolve CSS `var()` — and
 * recomputed when the theme class or the branding inline vars on `<html>`
 * change, so avatars re-tint uniformly on theme switch and match a branding
 * distributor's brand. Upload/override is intentionally not wired yet; this is
 * the default-only step.
 */

import { useEffect, useState } from "react";
import BoringAvatar from "boring-avatars";

import { cn } from "@/lib/utils";

type Variant =
  | "marble"
  | "beam"
  | "bauhaus"
  | "pixel"
  | "ring"
  | "sunset"
  | "geometric"
  | "abstract";

// Spread across the theme's accent + chart ramp for a balanced palette. These
// are the same tokens branding overrides, so avatars follow a distributor brand.
const PALETTE_VARS = [
  "--primary",
  "--chart-2",
  "--chart-3",
  "--chart-4",
  "--chart-5",
] as const;

function readPalette(): string[] {
  if (typeof document === "undefined") return [];
  const s = getComputedStyle(document.documentElement);
  return PALETTE_VARS.map((v) => {
    const raw = s.getPropertyValue(v).trim();
    return raw ? `hsl(${raw})` : null;
  }).filter((x): x is string => x !== null);
}

/** Live theme palette that re-reads when the theme class / branding vars change. */
function useThemePalette(): string[] {
  const [palette, setPalette] = useState<string[]>(readPalette);
  useEffect(() => {
    const root = document.documentElement;
    const update = () => setPalette(readPalette());
    update(); // catch any paint between initial state and effect
    const observer = new MutationObserver(update);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    return () => observer.disconnect();
  }, []);
  return palette;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface UserAvatarProps {
  /** Stable per-user seed — same input always yields the same avatar. */
  seed: string;
  /** Display name, used for the accessible label and the fallback initials. */
  name?: string;
  /** Rendered size in px (defaults to 28). */
  size?: number;
  variant?: Variant;
  className?: string;
  /**
   * The person's uploaded photo. Drawn instead of the generated picture when
   * it loads; a photo that fails to load falls back to the generated one, so
   * a broken image never stands in for a person.
   */
  src?: string | null;
}

export function UserAvatar({
  seed,
  name,
  size = 28,
  variant = "marble",
  className,
  src,
}: UserAvatarProps) {
  const palette = useThemePalette();
  const label = name?.trim() || seed;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showPhoto = Boolean(src) && src !== failedSrc;

  // No hover text. Every avatar in the product sits beside the name it
  // stands for, or inside a control that has its own tooltip, so a browser
  // title only repeated the name and stacked a second tooltip on the first.
  return (
    <span
      className={cn(
        "inline-flex shrink-0 overflow-hidden rounded-full",
        className,
      )}
      style={{ width: size, height: size }}
      aria-label={label}
      role="img"
    >
      {showPhoto ? (
        <img
          src={src ?? undefined}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setFailedSrc(src ?? null)}
        />
      ) : palette.length > 0 ? (
        <BoringAvatar
          size={size}
          name={seed || label}
          variant={variant}
          colors={palette}
          square
          // Inline size beats any ancestor `[&_svg]:size-4` utility (e.g. the
          // header menu button) that would otherwise shrink the SVG below the
          // circle and leave a gap. `block` avoids inline-baseline slack.
          style={{ width: size, height: size, display: "block" }}
        />
      ) : (
        // Rare: theme vars not yet computed — keep a readable fallback.
        <span
          className="flex h-full w-full items-center justify-center bg-accent font-medium text-foreground"
          style={{ fontSize: Math.max(9, Math.round(size * 0.38)) }}
        >
          {initials(label)}
        </span>
      )}
    </span>
  );
}
